import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrency } from "../../context/CurrencyContext";
import { getPackagesFromDB } from "../../services/pricingService";
import EmailVerify from "./Components/Dashbord/EmailVerify";
import "./Packages.css";
import { getuserdata } from "../../services/apiService";
import { getSettings } from "../../services/settingsService";
import { oneKlicks } from "../../SDK/oneclick";

const Packages = () => {
    const [userdata, setUserdata] = useState({});
    const [tabsest, setTabseset] = useState("Personal Details");
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const { selectedCurrency, formatPrice, convertPrice, isLoading: currencyLoading } = useCurrency();
    const [razorpayKey, setRazorpayKey] = useState('rzp_live_SxrHxAcaqcutXT'); // fallback

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                // Load user data
                const userData = await getuserdata('full');
                if (userData) {
                    setUserdata(userData);
                }
            } catch (error) {
                console.error("Error loading initial data:", error);
            }
        };

        loadInitialData();
        fetchPackages();
    }, []);

    const fetchPackages = async () => {
        try {
            setLoading(true);
            const packagesData = await getPackagesFromDB();
            if (packagesData && packagesData.length > 0) {
                setPackages(packagesData);
            } else {
                setPackages([]);
            }
        } catch (error) {
            console.error('Error fetching packages:', error);
            setPackages([]);
        } finally {
            setLoading(false);
        }
    };

    const getDisplayPrice = async (pkg) => {
        if (pkg.baseCurrency === selectedCurrency) {
            return { amount: pkg.price, symbol: getCurrencySymbol(selectedCurrency) };
        }

        try {
            const convertedAmount = await convertPrice(pkg.price, pkg.baseCurrency, selectedCurrency);
            return { amount: convertedAmount, symbol: getCurrencySymbol(selectedCurrency) };
        } catch (error) {
            console.error('Price conversion failed:', error);
            return { amount: pkg.price, symbol: getCurrencySymbol(pkg.baseCurrency) };
        }
    };

    const getCurrencySymbol = (currency) => {
        const currencyMap = {
            'USD': '$', 'EUR': '€', 'GBP': '£', 'INR': '₹',
            'JPY': '¥', 'AUD': 'A$', 'CAD': 'C$'
        };
        return currencyMap[currency] || '₹';
    };

    if (loading) {
        return (
            <div className="packages-container">
                <div className="packages-header">
                    <h1>Loading Packages...</h1>
                    <div className="loading-spinner">⏳</div>
                </div>
            </div>
        );
    }

    return (
        <div className="packages-container">
            {userdata.activation_key == null && <EmailVerify />}

            <div className="packages-header">
                <h1>Choose Your Package</h1>
                <p>Select the perfect plan for your needs</p>
                <div className="currency-note" style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                    Prices shown in {selectedCurrency}
                    {currencyLoading && <span style={{ marginLeft: '0.5rem' }}>🔄</span>}
                </div>
            </div>

            <div className="packages-grid">
                {packages.map((pkg) => (
                    <PackageCard
                        key={pkg.key}
                        pkg={pkg}
                        selectedCurrency={selectedCurrency}
                        getDisplayPrice={getDisplayPrice}
                        getCurrencySymbol={getCurrencySymbol}
                        userdata={userdata}
                    />
                ))}
            </div>
        </div>
    );
};

// Separate component for package card with dynamic pricing
const PackageCard = ({ pkg, selectedCurrency, getDisplayPrice, getCurrencySymbol, userdata }) => {
    const navigate = useNavigate();
    const [displayPrice, setDisplayPrice] = useState(null);
    const [priceLoading, setPriceLoading] = useState(true);

    useEffect(() => {
        loadPrice();
    }, [pkg, selectedCurrency]);

    const loadPrice = async () => {
        setPriceLoading(true);
        try {
            const price = await getDisplayPrice(pkg);
            setDisplayPrice(price);
        } catch (error) {
            console.error('Error loading price:', error);
            setDisplayPrice({ amount: pkg.price, symbol: getCurrencySymbol(pkg.baseCurrency) });
        } finally {
            setPriceLoading(false);
        }
    };

    if (!displayPrice) {
        return (
            <div className="package-card">
                <div className="package-header">
                    <h3>{pkg.name}</h3>
                    <div className="package-price">
                        <span className="loading-price">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    const handleCheckout = () => {
        navigate(`/doctor/package-checkout/${pkg.id}`);
    };

    const isPlatinum = pkg.name.toLowerCase().includes('platinum');

    return (
        <div className={`package-card ${isPlatinum ? 'is-featured' : ''}`} style={{ position: 'relative' }}>
            {isPlatinum && <div className="package-badge" style={{ position: 'absolute', top: '-12px', right: '10px', background: '#ff4d4f', color: '#fff', padding: '4px 12px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 'bold', zIndex: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>MOST POPULAR</div>}
            {pkg.image && (
                <div className="package-image">
                    <img
                        src={pkg.image.startsWith('http') ? pkg.image : `https://apis.oneklicks.com${pkg.image}`}
                        alt={pkg.name}
                        style={{ width: '100%', height: '140px', borderTopLeftRadius: '10px', borderTopRightRadius: '10px', objectFit: 'cover' }}
                    />
                </div>
            )}
            <div className="package-header" style={{ paddingTop: pkg.image ? '15px' : '30px' }}>
                <h3>{pkg.name}</h3>
                <div className="package-price">
                    <span className="currency">{displayPrice.symbol}</span>
                    <span className="amount">
                        {priceLoading ? '...' : displayPrice.amount.toFixed(2)}
                    </span>
                    <span className="period">/{pkg.period}</span>
                </div>
                {selectedCurrency !== pkg.baseCurrency && (
                    <div className="original-price" style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                        Original: {getCurrencySymbol(pkg.baseCurrency)}{pkg.price}
                    </div>
                )}
            </div>

            <div className="package-features">
                <ul>
                    <li><span className="feature-label">Professional Website</span> <span className="feature-value">{pkg.professionalwebsite?.trim().toLowerCase() === "yes" ? `✓ (${pkg.website_pages} pages)` : '✗'}</span></li>
                    <li><span className="feature-label">SMS</span> <span className="feature-value">{pkg.sms}</span></li>
                    <li><span className="feature-label">WhatsApp Messages</span> <span className="feature-value">{pkg.whatsup_count}</span></li>
                    <li><span className="feature-label">Emails</span> <span className="feature-value">{pkg.email}</span></li>
                    <li><span className="feature-label">Patient Count</span> <span className="feature-value">{pkg.patient_count}</span></li>
                    <li><span className="feature-label">Social Posts</span> <span className="feature-value">{pkg.socialposts}</span></li>
                    <li><span className="feature-label">Social Videos</span> <span className="feature-value">{pkg.socialvideos}</span></li>
                    <li><span className="feature-label">Auto Post</span> <span className="feature-value">{pkg.autopost?.trim().toLowerCase() === "yes" ? '✓' : '✗'}</span></li>
                    <li><span className="feature-label">Chat Support</span> <span className="feature-value">{pkg.chatsupport?.trim().toLowerCase() === "yes" ? '✓' : '✗'}</span></li>
                    <li><span className="feature-label">HMS Integration</span> <span className="feature-value">{pkg.hms?.trim().toLowerCase() === "yes" ? '✓' : '✗'}</span></li>
                    <li><span className="feature-label">Featured Profile</span> <span className="feature-value">{pkg.featuredprofile?.trim().toLowerCase() === "yes" ? '✓' : '✗'}</span></li>
                    <li><span className="feature-label">Featured Days</span> <span className="feature-value">{pkg.featureddays}</span></li>
                </ul>
            </div>

            {String(userdata.package_id) === String(pkg.id) ? (
                <button className="package-button" disabled style={{ background: '#10b981', cursor: 'default' }}>
                    Current Package
                </button>
            ) : (
                <button className="package-button" data-key={pkg.key} onClick={handleCheckout}>
                    {pkg.name === "Free" ? "Get Started" : "Choose Plan"}
                </button>
            )}
        </div>
    );
};

export default Packages;
